import axios, { AxiosError } from 'axios';
import type { Popup } from '../types/popup.types';

const API_BASE_URL = process.env.REACT_APP_API_CMS_URL || 'http://localhost:8004';
const CACHE_DURATION = 5 * 60 * 1000; // 5 dakika

interface CacheEntry {
  data: Popup[];
  timestamp: number;
}

class PopupService {
  private cache = new Map<number, CacheEntry>();
  private abortControllers = new Map<number, AbortController>();

  /**
   * Vendor için popup listesini getirir
   */
  async getPopupsForVendor(vendorId: number): Promise<Popup[]> {
    // Cache kontrolü
    const cached = this.cache.get(vendorId);
    if (cached && Date.now() - cached.timestamp < CACHE_DURATION) {
      return cached.data;
    }

    // Önceki request'i iptal et
    this.abortPreviousRequest(vendorId);

    // Yeni abort controller
    const abortController = new AbortController();
    this.abortControllers.set(vendorId, abortController);

    try {
      const response = await axios.get<Popup[]>(
        `${API_BASE_URL}/mc-popup/${vendorId}`,
        {
          signal: abortController.signal,
          timeout: 10000,
        }
      );

      // Cache'e kaydet
      this.cache.set(vendorId, {
        data: response.data,
        timestamp: Date.now(),
      });

      return response.data;
    } catch (error) {
      if (axios.isCancel(error)) {
        throw new Error('Request cancelled');
      }

      const axiosError = error as AxiosError;
      if (axiosError.code === 'ECONNABORTED') {
        throw new Error('Request timeout');
      }

      throw error;
    } finally {
      this.abortControllers.delete(vendorId);
    }
  }

  /**
   * Önceki request'i iptal et
   */
  private abortPreviousRequest(vendorId: number): void {
    const controller = this.abortControllers.get(vendorId);
    if (controller) {
      controller.abort();
      this.abortControllers.delete(vendorId);
    }
  }

  /**
   * Cache'i temizle
   */
  clearCache(vendorId?: number): void {
    if (vendorId) {
      this.cache.delete(vendorId);
    } else {
      this.cache.clear();
    }
  }
}

const popupServiceInstance = new PopupService();
export default popupServiceInstance;
